import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'

import {
  FUTURES_ALL_ROWS_PAGE_SIZE,
  useGetFuturesReportQuery,
  useRefreshCheckFuturesMutation,
  useSaveCheckFuturesMutation,
} from '../../store/api/futuresControlApi'
import { useSnackbar } from '../../hooks/useSnackbar'
import { futuresApiErrorMessage } from './futuresApiErrorMessage'
import {
  futuresFormDialogActionBtnSx,
  futuresFormDialogActionsBoxSx,
  futuresFormDialogTitleSx,
} from './futuresAccessUi'

interface FuturesChangeMonthDialogProps {
  open: boolean
  onClose: () => void
  /** Permission gating (Layer 4) — false → ẩn Save + read-only banner. */
  canEdit?: boolean
}

type Row = { futuresMonth: string; checked: boolean }

const variantTitleSx = {
  px: 1,
  py: 0.5,
  fontSize: '0.75rem',
  fontWeight: 800,
  letterSpacing: '0.02em',
  color: '#0b3633',
  textTransform: 'uppercase' as const,
  background: 'linear-gradient(180deg, #fff9f4 0%, #f4df9c 100%)',
  border: '1px solid rgba(11, 54, 51, 0.16)',
  borderBottom: '2px solid rgba(11, 54, 51, 0.34)',
  textAlign: 'center' as const,
}

const tableWrapSx = {
  border: '1px solid rgba(23, 35, 29, 0.08)',
  borderTop: 'none',
  borderBottomLeftRadius: 6,
  borderBottomRightRadius: 6,
  maxHeight: 360,
  overflowY: 'auto' as const,
  '& thead th': {
    position: 'sticky' as const,
    top: 0,
    zIndex: 1,
    backgroundColor: '#f8e6b0',
    backgroundImage: 'linear-gradient(180deg, #fff9f4 0%, #f4df9c 100%)',
    boxShadow: '0 1px 0 rgba(11, 54, 51, 0.32), inset 0 -1px 0 rgba(11, 54, 51, 0.32)',
  },
}

const tableSx = {
  '& th, & td': {
    fontSize: '0.7rem',
    py: 0.25,
    px: 0.75,
    color: '#0b3633',
    borderBottom: '1px solid rgba(23, 35, 29, 0.08)',
  },
  '& th': {
    fontWeight: 800,
    textAlign: 'left',
  },
  '& tbody tr:nth-of-type(even) td': {
    bgcolor: 'rgba(244, 223, 156, 0.18)',
  },
  '& tbody tr:hover td': {
    bgcolor: 'rgba(20, 90, 89, 0.09)',
    cursor: 'pointer',
  },
}

function VariantList({
  variant,
  rows,
  onToggle,
  onToggleAll,
}: {
  variant: 'LD' | 'NY'
  rows: Row[]
  onToggle: (futuresMonth: string) => void
  onToggleAll: (checked: boolean) => void
}) {
  const checkedCount = rows.filter((r) => r.checked).length
  const allChecked = rows.length > 0 && checkedCount === rows.length
  const someChecked = checkedCount > 0 && !allChecked

  return (
    <Box sx={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
      <Box sx={variantTitleSx}>{variant}</Box>
      <Box sx={tableWrapSx}>
        <Table size="small" sx={tableSx} stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ width: 36 }}>
                <Checkbox
                  size="small"
                  indeterminate={someChecked}
                  checked={allChecked}
                  onChange={(_, v) => onToggleAll(v)}
                  sx={{ p: 0.25 }}
                />
              </TableCell>
              <TableCell>Futures Month</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={2}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                    Không có dữ liệu — chạy lại Change Futures Month.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.futuresMonth} onClick={() => onToggle(row.futuresMonth)} hover>
                  <TableCell padding="checkbox" sx={{ width: 36 }}>
                    <Checkbox
                      size="small"
                      checked={row.checked}
                      onChange={() => onToggle(row.futuresMonth)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ p: 0.25 }}
                    />
                  </TableCell>
                  <TableCell>{row.futuresMonth}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>
      <Typography
        variant="caption"
        sx={{ mt: 0.5, color: 'text.secondary', fontSize: '0.65rem', textAlign: 'right' }}
      >
        {checkedCount} / {rows.length} selected
      </Typography>
    </Box>
  )
}

function rowsFromItems(items: unknown[] | undefined): Row[] {
  return ((items ?? []) as Record<string, unknown>[]).map((r) => ({
    futuresMonth: String(r.futuresMonth ?? r.futures_month ?? '').trim(),
    checked: Number(r.checkedRow ?? r.checked_row ?? 0) === -1,
  })).filter((r) => r.futuresMonth)
}

export default function FuturesChangeMonthDialog({ open, onClose, canEdit = true }: FuturesChangeMonthDialogProps) {
  const snack = useSnackbar()
  const [refreshCheckFutures, refreshState] = useRefreshCheckFuturesMutation()
  const [saveCheckFutures, saveState] = useSaveCheckFuturesMutation()
  const [hydrated, setHydrated] = useState(false)
  const [ldRows, setLdRows] = useState<Row[]>([])
  const [nyRows, setNyRows] = useState<Row[]>([])
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const ldQ = useGetFuturesReportQuery(
    { kind: 'check-futures', variant: 'ld', page: 1, pageSize: FUTURES_ALL_ROWS_PAGE_SIZE },
    { skip: !open || !hydrated },
  )
  const nyQ = useGetFuturesReportQuery(
    { kind: 'check-futures', variant: 'ny', page: 1, pageSize: FUTURES_ALL_ROWS_PAGE_SIZE },
    { skip: !open || !hydrated },
  )

  // On open: refresh tmp tables from market_calendar (btnNew_Click), then fetch.
  useEffect(() => {
    if (!open) {
      setHydrated(false)
      setErrorMsg(null)
      return
    }
    let cancelled = false
    void (async () => {
      try {
        await refreshCheckFutures().unwrap()
        if (!cancelled) setHydrated(true)
      } catch (err) {
        if (!cancelled) setErrorMsg(futuresApiErrorMessage(err))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, refreshCheckFutures])

  useEffect(() => {
    if (ldQ.data) setLdRows(rowsFromItems(ldQ.data.items))
  }, [ldQ.data])
  useEffect(() => {
    if (nyQ.data) setNyRows(rowsFromItems(nyQ.data.items))
  }, [nyQ.data])

  const toggle = (variant: 'ld' | 'ny', futuresMonth: string) => {
    const setter = variant === 'ld' ? setLdRows : setNyRows
    setter((prev) => prev.map((r) => (r.futuresMonth === futuresMonth ? { ...r, checked: !r.checked } : r)))
  }
  const toggleAll = (variant: 'ld' | 'ny', checked: boolean) => {
    const setter = variant === 'ld' ? setLdRows : setNyRows
    setter((prev) => prev.map((r) => ({ ...r, checked })))
  }

  const loading = refreshState.isLoading || ldQ.isLoading || nyQ.isLoading
  const fetching = ldQ.isFetching || nyQ.isFetching
  const apiError = useMemo(() => {
    if (errorMsg) return errorMsg
    if (ldQ.error) return futuresApiErrorMessage(ldQ.error)
    if (nyQ.error) return futuresApiErrorMessage(nyQ.error)
    return null
  }, [errorMsg, ldQ.error, nyQ.error])

  const handleSave = async () => {
    if (!canEdit) {
      setErrorMsg("You don't have permission for this action.")
      return
    }
    setErrorMsg(null)
    try {
      await saveCheckFutures({
        ld: ldRows.filter((r) => r.checked).map((r) => r.futuresMonth),
        ny: nyRows.filter((r) => r.checked).map((r) => r.futuresMonth),
      }).unwrap()
      snack.success('Futures Month selection saved.')
      onClose()
      // saveCheckFutures đã invalidatesTags(['FuturesControl']) → cả 5 view
      // (cash/recon/pnl/orders/position) tự re-fetch theo is_show mới. Không
      // cần window.location.reload() (reload cả app, chậm + mất state).
    } catch (err) {
      setErrorMsg(futuresApiErrorMessage(err))
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={futuresFormDialogTitleSx}>
        <Box
          component="span"
          title="Change Futures Month"
          sx={{
            fontWeight: 800,
            fontSize: '0.95rem',
            color: 'primary.dark',
            flex: '1 1 auto',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          Change Futures Month
        </Box>
        {fetching && !loading ? <CircularProgress size={14} /> : null}
        <Box sx={futuresFormDialogActionsBoxSx}>
          <Button
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={!canEdit || loading || saveState.isLoading}
            sx={futuresFormDialogActionBtnSx}
          >
            {saveState.isLoading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={onClose}
            disabled={saveState.isLoading}
            sx={futuresFormDialogActionBtnSx}
          >
            Cancel
          </Button>
        </Box>
      </DialogTitle>
      <Divider sx={{ flexShrink: 0 }} />
      <DialogContent dividers sx={{ p: 1.5 }}>
        {!canEdit ? (
          <Alert severity="info" sx={{ mb: 1, fontSize: '0.72rem' }}>
            Read-only — you don't have edit permission for this module.
          </Alert>
        ) : null}
        {apiError ? (
          <Alert severity="error" sx={{ mb: 1, fontSize: '0.72rem' }}>
            {apiError}
          </Alert>
        ) : null}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <VariantList
              variant="LD"
              rows={ldRows}
              onToggle={(m) => toggle('ld', m)}
              onToggleAll={(v) => toggleAll('ld', v)}
            />
            <VariantList
              variant="NY"
              rows={nyRows}
              onToggle={(m) => toggle('ny', m)}
              onToggleAll={(v) => toggleAll('ny', v)}
            />
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  )
}
